import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Clock, ArrowUpRight, ArrowDownRight,
  Loader2, Filter, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, DollarSign
} from 'lucide-react';
import { Button } from '../components/ui/button';
import DashboardLayout from '../components/DashboardLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TransactionHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Pagination & Filters
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [filterType, setFilterType] = useState('all');

  // Stats
  const [stats, setStats] = useState({ deposits: 0, prizes: 0, wagers: 0 });

  const filterOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'credit_purchase', label: 'Deposits' },
    { value: 'withdrawal', label: 'Withdrawals' },
    { value: 'entry_fee', label: 'Entry Fees' },
    { value: 'prize_award', label: 'Rewards Won' },
    { value: 'credit_refund', label: 'Refunds' }
  ];

  useEffect(() => {
    fetchTransactions();
  }, [page, filterType]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const offset = (page - 1) * limit;
      let url = `${API}/wallet/transactions?limit=${limit}&offset=${offset}`;
      if (filterType !== 'all') {
        url += `&type=${filterType}`;
      }

      const res = await axios.get(url, { withCredentials: true });
      setTransactions(res.data.transactions || []);
      setTotalCount(res.data.total || 0);
      setHasMore(res.data.has_more || false);

      // Calculate basic stats from this page if it's the first page without filters
      // (For a production app, we'd have a separate stats endpoint)
      if (page === 1 && filterType === 'all' && res.data.transactions) {
        let deps = 0, prz = 0, wag = 0;
        res.data.transactions.forEach(t => {
          if (t.type === 'credit_purchase') deps += t.amount;
          if (t.type === 'prize_award') prz += t.amount;
          if (t.type === 'entry_fee') wag -= t.amount; // entry_fee is negative
        });
        setStats({ deposits: deps, prizes: prz, wagers: wag });
      }

    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setPage(1); // Reset to first page
  };

  const formatAmount = (amount, type) => {
    const isPositive = amount > 0;
    const prefix = isPositive ? '+' : '';
    const formatted = Math.abs(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });

    // withdrawal and prize_award use USD ($), others use credits (cr)
    const isUSD = type === 'withdrawal' || type === 'prize_award' || type === 'cash_update';

    return `${prefix}${isUSD ? '$' : ''}${formatted}${!isUSD ? ' cr' : ''}`;
  };

  const getTransactionIcon = (type, amount) => {
    if (type === 'credit_purchase') return <TrendingUp className="w-5 h-5 text-[#00FF94]" />;
    if (type === 'cash_to_credits_conversion') return <ArrowUpRight className="w-5 h-5 text-[#00F0FF]" />;
    if (type === 'withdrawal') return <TrendingDown className="w-5 h-5 text-[#FFB800]" />;
    if (type === 'prize_award') return <DollarSign className="w-5 h-5 text-[#00F0FF]" />;

    return amount > 0 ?
      <ArrowDownRight className="w-5 h-5 text-[#00FF94]" /> :
      <ArrowUpRight className="w-5 h-5 text-[#FF2E2E]" />;
  };

  const getTransactionLabel = (type) => {
    const labels = {
      credit_purchase: 'Credits Purchase',
      cash_to_credits_conversion: 'Converted to Credits',
      withdrawal: 'Cash Withdrawal',
      entry_fee: 'Match Entry Fee',
      prize_award: 'Competition Reward',
      credit_refund: 'Refund',
      cash_update: 'Cash Adjust',
      credit_update: 'Credit Adjust'
    };
    return labels[type] || type.replace('_', ' ');
  };

  const totalPages = Math.ceil(totalCount / limit);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto">
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/wallet')}
              className="bg-white/5 hover:bg-white/10 rounded-full w-10 h-10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading font-bold text-3xl tracking-tight">Transaction History</h1>
              <p className="text-white/40 text-sm mt-0.5">Track your earnings and expenditures</p>
            </div>
          </div>

          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 w-fit">
            <Clock className="w-4 h-4 text-[#00F0FF]" />
            <span className="text-xs font-mono text-white/70">Updated: Just now</span>
          </div>
        </div>

        {/* Stats Summary - Premium Cards */}
        {page === 1 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }}
              className="glass-card rounded-2xl p-5 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <TrendingUp className="w-16 h-16" />
              </div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-12 h-12 rounded-xl bg-[#00FF94]/20 border border-[#00FF94]/30 flex items-center justify-center shadow-[0_0_20px_rgba(0,255,148,0.15)]">
                  <TrendingUp className="w-6 h-6 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-white/40 text-xs font-medium uppercase tracking-wider">Deposits</p>
                  <p className="font-mono font-bold text-xl text-[#00FF94]">+{stats.deposits.toLocaleString()} <span className="text-[10px] opacity-70">CR</span></p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="glass-card rounded-2xl p-5 relative overflow-hidden group"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <DollarSign className="w-16 h-16" />
              </div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-12 h-12 rounded-xl bg-[#00F0FF]/20 border border-[#00F0FF]/30 flex items-center justify-center shadow-[0_0_20px_rgba(0,240,255,0.15)]">
                  <DollarSign className="w-6 h-6 text-[#00F0FF]" />
                </div>
                <div>
                  <p className="text-white/40 text-xs font-medium uppercase tracking-wider">Rewards Won</p>
                  <p className="font-mono font-bold text-xl text-[#00F0FF]">+${stats.prizes.toLocaleString()}</p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="glass-card rounded-2xl p-5 relative overflow-hidden group sm:col-span-2 lg:col-span-1"
            >
              <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                <TrendingDown className="w-16 h-16" />
              </div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-12 h-12 rounded-xl bg-[#FF2E2E]/20 border border-[#FF2E2E]/30 flex items-center justify-center shadow-[0_0_20px_rgba(255,46,46,0.15)]">
                  <TrendingDown className="w-6 h-6 text-[#FF2E2E]" />
                </div>
                <div>
                  <p className="text-white/40 text-xs font-medium uppercase tracking-wider">Entry Fees</p>
                  <p className="font-mono font-bold text-xl text-[#FF2E2E]">-{Math.abs(stats.wagers).toLocaleString()} <span className="text-[10px] opacity-70">CR</span></p>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Transactions Table Body */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
          className="glass-card rounded-2xl border border-white/10 overflow-hidden shadow-2xl"
        >
          {/* Filters Bar */}
          <div className="bg-white/[0.02] p-4 sm:px-6 border-b border-white/10 overflow-x-auto hide-scrollbar">
            <div className="flex items-center gap-3 min-w-max">
              <div className="flex items-center text-white/40 mr-2 bg-white/5 px-2 py-1.5 rounded-lg border border-white/5">
                <Filter className="w-3.5 h-3.5 mr-2" />
                <span className="text-[10px] uppercase font-bold tracking-widest">Filters</span>
              </div>
              <div className="flex gap-2">
                {filterOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => handleFilterChange(opt.value)}
                    className={`px-4 py-1.5 rounded-xl text-xs font-semibold transition-colors duration-200 border ${filterType === opt.value
                        ? 'bg-[#00F0FF] text-black border-[#00F0FF] shadow-[0_0_15px_rgba(0,240,255,0.3)]'
                        : 'bg-white/5 text-white/50 border-white/10 hover:border-white/20 hover:text-white'
                      }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Transactions List */}
          <div className="divide-y divide-white/5">
            {loading ? (
              <div className="py-24 flex flex-col items-center justify-center gap-4">
                <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin" />
                <p className="text-sm text-white/30 font-mono animate-pulse">Synchronizing ledger...</p>
              </div>
            ) : transactions.length === 0 ? (
              <div className="py-24 text-center">
                <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6 border border-white/5">
                  <Clock className="w-10 h-10 text-white/20" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">No Transactions Found</h3>
                <p className="text-white/40 text-sm max-w-xs mx-auto mb-8">It looks like you haven't performed any activities under this category yet.</p>
                {filterType !== 'all' && (
                  <Button
                    onClick={() => handleFilterChange('all')}
                    className="btn-primary clip-slant-sm"
                  >
                    View All History
                  </Button>
                )}
              </div>
            ) : (
              <div className="bg-transparent">
                {transactions.map((txn, idx) => (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    key={txn.transaction_id || idx}
                    className="flex items-center justify-between p-4 sm:p-6 hover:bg-white/[0.03] transition-all group"
                  >
                    <div className="flex items-center gap-4 sm:gap-6">
                      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 transition-colors ${txn.amount > 0
                          ? 'bg-[#00FF94]/10 border border-[#00FF94]/20'
                          : 'bg-[#FF2E2E]/10 border border-[#FF2E2E]/20'
                        }`}>
                        {getTransactionIcon(txn.type, txn.amount)}
                      </div>

                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <p className="font-bold text-sm sm:text-base text-white group-hover:text-[#00F0FF] transition-colors">
                            {getTransactionLabel(txn.type)}
                          </p>
                          {txn.match_id && (
                            <span className="text-[9px] sm:text-[10px] px-2 py-0.5 rounded-md bg-[#7000FF]/20 text-[#7000FF] border border-[#7000FF]/30 font-bold uppercase tracking-wider">
                              MATCH: {txn.match_id.substring(0, 8)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-1.5">
                          <Clock className="w-3 h-3 text-white/20" />
                          <p className="text-white/30 text-[10px] sm:text-[11px] font-medium uppercase tracking-tight">
                            {new Date(txn.created_at).toLocaleString([], {
                              month: 'short', day: 'numeric', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p className={`font-mono text-base sm:text-lg font-bold tracking-tight ${txn.amount > 0 ? 'text-[#00FF94]' : 'text-white'
                        }`}>
                        {formatAmount(txn.amount, txn.type)}
                      </p>
                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        <div className="w-1 h-1 rounded-full bg-white/20" />
                        <p className="text-white/50 text-[10px] sm:text-[11px] font-mono font-bold uppercase">
                          Bal: {txn.balance_after.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Premium Pagination */}
          {!loading && totalPages > 1 && (
            <div className="p-4 sm:p-6 bg-white/[0.01] border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-white/30">
                Page <span className="text-white/60">{page}</span> of <span className="text-white/60">{totalPages}</span> — <span className="text-white/60">{totalCount}</span> Total TX
              </p>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1 || loading}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  className="bg-white/5 border-white/10 hover:bg-[#00F0FF]/10 hover:border-[#00F0FF]/30 hover:text-[#00F0FF] transition-all h-9 px-4 rounded-xl"
                >
                  <ChevronLeft className="w-4 h-4 mr-1.5" />
                  PREV
                </Button>
                <div className="flex gap-1">
                  {[...Array(Math.min(3, totalPages))].map((_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-9 h-9 rounded-xl flex items-center justify-center text-xs font-bold transition-all ${page === pageNum
                            ? 'bg-[#00F0FF] text-black shadow-[0_0_10px_rgba(0,240,255,0.2)]'
                            : 'bg-white/5 text-white/40 hover:bg-white/10'
                          }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  {totalPages > 3 && <span className="text-white/20 flex items-center px-1">...</span>}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!hasMore || loading}
                  onClick={() => setPage(p => p + 1)}
                  className="bg-white/5 border-white/10 hover:bg-[#00F0FF]/10 hover:border-[#00F0FF]/30 hover:text-[#00F0FF] transition-all h-9 px-4 rounded-xl"
                >
                  NEXT
                  <ChevronRight className="w-4 h-4 ml-1.5" />
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default TransactionHistory;
